import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import { liste as kundenliste } from '../api/kunden';
import { alsTag } from '../datum';
import {
  type Preisliste,
  type Preislistenfelder,
  type Preislistenzuordnung,
  type Rangstufe,
  RANGSTUFENNAME,
  preislisteAendern,
  preislisteAnlegen,
  preislisteLoeschen,
  preislisten as holePreislisten,
  rabattgruppen as holeRabattgruppen,
  zuordnungAnlegen,
  zuordnungLoeschen,
  zuordnungen as holeZuordnungen,
} from '../api/produkte';
import { Dialog } from '../komponenten/Dialog';
import { Meldung } from '../komponenten/Meldung';
import { Liste } from '../komponenten/Liste';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';

/**
 * Preislistenverwaltung (PRD-13, PRF-02 bis PRF-04, SYS-10).
 *
 * ============================================================================
 * DIE RANGFOLGE IST DIE MASKE
 * ============================================================================
 *
 * PRF-02 legt fest, welche von mehreren gültigen Listen gewinnt:
 * kundenindividuell, Kundengruppe, Aktion, Vertriebsform, Standard — in
 * dieser Reihenfolge. Genau so stehen die Zeilen hier, und zwar
 * **absichtlich nicht alphabetisch**: Wer wissen will, welchen Preis ein
 * Kunde bekommt, liest von oben nach unten und hört bei der ersten Liste
 * auf, die auf ihn zutrifft.
 *
 * **PRF-03** entscheidet innerhalb derselben Stufe nach dem späteren
 * Gültigkeitsbeginn — deshalb die zweite Sortierung.
 *
 * **PRF-04** lässt eine Liste als *vorrangig* kennzeichnen; sie schlägt dann
 * die ganze Rangfolge. Höchstens eine darf zu einem Stichtag vorrangig sein.
 * Das hält die Datenbank selbst (`preisliste_hoechstens_eine_vorrangige`) —
 * hier steht keine zweite Fassung derselben Regel, sondern nur der Hinweis
 * darauf, was die Kennzeichnung bedeutet.
 *
 * ============================================================================
 * BEENDEN STATT LÖSCHEN
 * ============================================================================
 *
 * Eine Liste mit Preiszeilen wird nicht gelöscht. Sie zu löschen nähme jedem
 * Artikel darunter seinen Preis, und die Preisfindung fiele stillschweigend
 * auf die nächste Rangstufe — der Kunde bekäme einen anderen Preis, und
 * niemand sähe warum. Der richtige Weg ist ein **Gültig bis**; die Liste
 * bleibt dann in jeder alten Preisherleitung nachvollziehbar (PRF-13).
 *
 * ============================================================================
 * SEITENTYP LISTE (ÄA-41)
 * ============================================================================
 *
 * Zähler im Seitenkopf; „auch beendete" und „Preisliste anlegen" in der
 * Leiste; die Rangfolge als Tabelle in einer Karte. Liste und Zuordnungen
 * werden weiter im Fenster gepflegt — so wie bisher, und aus demselben Grund
 * wie bei den Warengruppen: Ein Entwurf darf nicht mit der Zeile wechseln.
 */
export function Preislisten({
  darfKonfigurieren,
}: {
  readonly darfKonfigurieren: boolean;
}): JSX.Element {
  const [listen, setzeListen] = useState<readonly Preisliste[]>([]);
  const [auchBeendete, setzeAuchBeendete] = useState(false);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [meldung, setzeMeldung] = useState('');

  const [offen, setzeOffen] = useState<Preisliste | 'neu' | null>(null);
  const [formular, setzeFormular] = useState<Formular>(leer());
  const [laeuft, setzeLaeuft] = useState(false);

  /** Welche Liste ihre Zuordnungen zeigt (PRD-13). */
  const [zuordnungenVon, setzeZuordnungenVon] = useState<Preisliste | null>(null);
  /** CRM-104: die Liste, deren Löschen im Pop-up nachfragt. */
  const [zuLoeschen, setzeZuLoeschen] = useState<Preisliste | null>(null);

  const laden = useCallback(async (): Promise<void> => {
    setzeLaedt(true);
    try {
      setzeListen(await holePreislisten(auchBeendete));
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaedt(false);
    }
  }, [auchBeendete]);

  useEffect(() => {
    void laden();
  }, [laden]);

  function oeffne(liste: Preisliste | 'neu'): void {
    setzeMeldung('');
    setzeFehler(undefined);
    setzeOffen(liste);
    setzeFormular(liste === 'neu' ? leer() : ausListe(liste));
  }

  async function absenden(ereignis: React.FormEvent): Promise<void> {
    ereignis.preventDefault();
    if (offen === null) return;
    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      const felder: Preislistenfelder = {
        bezeichnung: formular.bezeichnung.trim(),
        rangstufe: formular.rangstufe,
        gueltigAb: formular.gueltigAb,
        gueltigBis: formular.gueltigBis === '' ? null : formular.gueltigBis,
        vorrangig: formular.vorrangig,
      };
      if (offen === 'neu') {
        await preislisteAnlegen({ ...felder, kennung: formular.kennung.trim() });
        setzeMeldung(`Preisliste ${formular.kennung.trim()} angelegt.`);
      } else {
        await preislisteAendern(offen.id, felder);
        setzeMeldung(`Preisliste ${offen.kennung} geändert.`);
      }
      setzeOffen(null);
      await laden();
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  /** Wirft (CRM-104): Der Fehler steht in der Rückfrage, und sie bleibt offen. */
  async function loeschen(liste: Preisliste): Promise<void> {
    setzeMeldung('');
    setzeFehler(undefined);
    await preislisteLoeschen(liste.id);
    setzeZuLoeschen(null);
    setzeMeldung(`Preisliste ${liste.kennung} gelöscht.`);
    await laden();
  }

  const heute = new Date().toISOString().slice(0, 10);

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(laedt ? undefined : zaehlwort(listen.length, 'Preisliste', 'Preislisten'));

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}

      <Liste
        filter={
          <>
            <label className="liste__schalter">
              <input
                type="checkbox"
                checked={auchBeendete}
                onChange={(e) => {
                  setzeAuchBeendete(e.target.checked);
                }}
              />
              auch beendete
            </label>
            {darfKonfigurieren && (
              <button className="knopf knopf--haupt" type="button" onClick={() => oeffne('neu')}>
                Preisliste anlegen
              </button>
            )}
          </>
        }
        {...(auchBeendete
          ? { marken: [{ text: 'auch beendete', entfernen: () => setzeAuchBeendete(false) }] }
          : {})}
        hinweise={
          <>
            {/* PRF-02 bis PRF-04 */}
            <p className="hilfe">
              Treffen mehrere gültige Listen zu, gewinnt die oberste dieser Reihenfolge.
              Innerhalb einer Stufe gewinnt der spätere Gültigkeitsbeginn;
              eine vorrangige Liste schlägt die ganze Rangfolge.
            </p>
            {meldung !== '' && (
              <p className="hinweiszeile" role="status">
                {meldung}
              </p>
            )}
          </>
        }
      >
        {laedt ? (
          <p role="status">Wird geladen …</p>
        ) : listen.length === 0 ? (
          <div className="liste__leer">
            {/* ANG-05 */}
            <p className="hinweiszeile">
              Es ist keine Preisliste vorhanden. Ohne Liste findet die Preisfindung nichts
              und jeder Preis müsste an der Position übersteuert werden.
            </p>
          </div>
        ) : (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Preislisten">
              <thead>
                <tr>
                  <th scope="col">Rangstufe</th>
                  <th scope="col">Kennung</th>
                  <th scope="col">Bezeichnung</th>
                  <th scope="col">Gültig</th>
                  <th scope="col">Preiszeilen</th>
                  <th scope="col">Gilt für</th>
                  <th scope="col">Handlung</th>
                </tr>
              </thead>
              <tbody>
                {listen.map((l) => {
                  const beendet = l.gueltigBis !== null && l.gueltigBis < heute;
                  return (
                    <tr key={l.id} className={beendet ? 'liste__zeile--blass' : undefined}>
                      <td>
                        {RANGSTUFENNAME[l.rangstufe]}
                        {/*
                          PRF-04: Die Kennzeichnung schlägt die Rangfolge. Sie ist
                          eine WERTUNG und trägt deshalb eine Marke — anders als
                          die Rangstufe daneben, die nur einordnet.
                        */}
                        {l.vorrangig && <span className="marke marke--warn"> vorrangig</span>}
                      </td>
                      <th scope="row" className="liste__nummer">
                        <code>{l.kennung}</code>
                      </th>
                      <td>{l.bezeichnung}</td>
                      <td>
                        <span className="liste__einzeilig">ab {alsTag(l.gueltigAb)}</span>
                        {l.gueltigBis === null ? (
                          <span className="hilfe"> · unbefristet</span>
                        ) : (
                          <>
                            {' '}
                            <span className="liste__einzeilig">bis {alsTag(l.gueltigBis)}</span>
                          </>
                        )}
                      </td>
                      <td className="zahl">{l.preiszeilen}</td>
                      <td>
                        {l.zuordnungen === 0 ? (
                          <span className="hilfe">alle (keine Zuordnung)</span>
                        ) : (
                          `${String(l.zuordnungen)} Zuordnungen`
                        )}
                      </td>
                      <td>
                        <button
                          className="knopf knopf--klein"
                          type="button"
                          onClick={() => {
                            setzeMeldung('');
                            setzeZuordnungenVon(l);
                          }}
                        >
                          Zuordnungen
                        </button>{' '}
                        {darfKonfigurieren && (
                          <>
                            <button
                              className="knopf knopf--klein"
                              type="button"
                              onClick={() => oeffne(l)}
                            >
                              Bearbeiten
                            </button>{' '}
                            <button
                              className="knopf knopf--eingriff knopf--klein"
                              type="button"
                              disabled={l.preiszeilen > 0}
                              onClick={() => {
                                setzeMeldung('');
                                setzeZuLoeschen(l);
                              }}
                            >
                              Löschen …
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Liste>

      {offen !== null && (
        <Dialog
          titel={offen === 'neu' ? 'Neue Preisliste' : `Preisliste ${offen.kennung}`}
          unterzeile={offen === 'neu' ? undefined : offen.bezeichnung}
          breite="mittel"
          beiSchliessen={() => setzeOffen(null)}
        >
          <form onSubmit={(e) => void absenden(e)}>
            {offen === 'neu' ? (
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="plKennung">
                  Kennung <span className="feld__pflicht">Pflicht</span>
                </label>
                <input
                  id="plKennung"
                  className="feld__eingabe"
                  value={formular.kennung}
                  onChange={(e) => {
                    setzeFormular({ ...formular, kennung: e.target.value.toUpperCase() });
                  }}
                  placeholder="AKTION-HERBST"
                />
                <p className="feld__hilfe">
                  Unter ihr erscheint die Liste in der Preisherleitung. Danach{' '}
                  <strong>nicht mehr änderbar</strong>: Eine Herleitung, die auf eine
                  umbenannte Liste zeigt, lässt sich nicht mehr nachvollziehen.
                </p>
              </div>
            ) : (
              <div className="feld">
                <span className="feld__beschriftung">Kennung</span>
                <p>
                  <code>{offen.kennung}</code>
                </p>
              </div>
            )}

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="plBezeichnung">
                Bezeichnung <span className="feld__pflicht">Pflicht</span>
              </label>
              <input
                id="plBezeichnung"
                className="feld__eingabe"
                value={formular.bezeichnung}
                onChange={(e) => {
                  setzeFormular({ ...formular, bezeichnung: e.target.value });
                }}
              />
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="plRangstufe">
                Rangstufe
              </label>
              <select
                id="plRangstufe"
                className="feld__eingabe"
                value={formular.rangstufe}
                onChange={(e) => {
                  setzeFormular({ ...formular, rangstufe: e.target.value as Rangstufe });
                }}
              >
                {(Object.keys(RANGSTUFENNAME) as Rangstufe[]).map((r) => (
                  <option key={r} value={r}>
                    {RANGSTUFENNAME[r]}
                  </option>
                ))}
              </select>
              {/* PRF-02 */}
              <p className="feld__hilfe">
                In dieser Reihenfolge wird geprüft — kundenindividuell zuerst, Standard zuletzt.
              </p>
            </div>

            <div className="feldreihe feldreihe--halb">
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="plAb">
                  Gültig ab <span className="feld__pflicht">Pflicht</span>
                </label>
                <input
                  id="plAb"
                  type="date"
                  className="feld__eingabe"
                  value={formular.gueltigAb}
                  onChange={(e) => {
                    setzeFormular({ ...formular, gueltigAb: e.target.value });
                  }}
                />
              </div>
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="plBis">
                  Gültig bis
                </label>
                <input
                  id="plBis"
                  type="date"
                  className="feld__eingabe"
                  value={formular.gueltigBis}
                  onChange={(e) => {
                    setzeFormular({ ...formular, gueltigBis: e.target.value });
                  }}
                />
                <p className="feld__hilfe">
                  Leer heißt unbefristet. Eine Liste mit Preiszeilen wird beendet und nicht
                  gelöscht — sie bleibt in jeder alten Herleitung nachvollziehbar.
                </p>
              </div>
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="plVorrangig">
                <input
                  id="plVorrangig"
                  type="checkbox"
                  checked={formular.vorrangig}
                  onChange={(e) => {
                    setzeFormular({ ...formular, vorrangig: e.target.checked });
                  }}
                />{' '}
                Vorrangig
              </label>
              {/* PRF-04 schlaegt die Rangfolge aus PRF-02. */}
              <p className="feld__hilfe">
                Schlägt die Rangfolge. Zu einem Stichtag darf höchstens eine Liste vorrangig
                sein; eine zweite wird abgewiesen.
              </p>
            </div>

            <div className="feldreihe">
              <button className="knopf knopf--haupt" type="submit" disabled={laeuft}>
                {offen === 'neu' ? 'Anlegen' : 'Speichern'}
              </button>{' '}
              <button
                className="knopf knopf--verweis"
                type="button"
                onClick={() => setzeOffen(null)}
              >
                Abbrechen
              </button>
            </div>
          </form>
        </Dialog>
      )}

      {zuLoeschen !== null && (
        <Rueckfrage
          frage="Preisliste löschen?"
          unterzeile={`${zuLoeschen.kennung} · ${zuLoeschen.bezeichnung}`}
          tat="Löschen"
          beiBestaetigen={() => loeschen(zuLoeschen)}
          beiSchliessen={() => setzeZuLoeschen(null)}
        >
          <p>
            Die Liste hat keine Preiszeilen
            {zuLoeschen.zuordnungen === 0
              ? ' und keine Zuordnung.'
              : zuLoeschen.zuordnungen === 1
                ? '; ihre 1 Zuordnung verschwindet mit ihr.'
                : `; ihre ${String(zuLoeschen.zuordnungen)} Zuordnungen verschwinden mit ihr.`}{' '}
            Die Kennung wird wieder frei; zurückholen lässt sich die Liste hier nicht.
          </p>
        </Rueckfrage>
      )}

      {zuordnungenVon !== null && (
        <Zuordnungspflege
          liste={zuordnungenVon}
          darfKonfigurieren={darfKonfigurieren}
          beiSchliessen={() => {
            setzeZuordnungenVon(null);
            void laden();
          }}
        />
      )}
    </>
  );
}

/**
 * Wer diese Preisliste bekommt (PRD-13).
 *
 * ENTWEDER EIN KUNDE ODER EINE RABATTGRUPPE, nie beides und nie keines: Eine
 * Zuordnung ohne Ziel gälte für niemanden, eine mit zwei Zielen für zwei
 * verschiedene Mengen. Die Bedingung steht an der Tabelle; die Maske bietet
 * deshalb eine Umschaltung und keine zwei Felder nebeneinander.
 *
 * OHNE ZUORDNUNG GILT DIE LISTE FÜR ALLE. Das ist kein Versehen, sondern der
 * Regelfall für die Standardliste — und es steht in der Übersicht, damit
 * niemand eine leere Zuordnungsliste für einen Fehler hält.
 */
function Zuordnungspflege({
  liste,
  darfKonfigurieren,
  beiSchliessen,
}: {
  readonly liste: Preisliste;
  readonly darfKonfigurieren: boolean;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [zeilen, setzeZeilen] = useState<readonly Preislistenzuordnung[]>([]);
  const [gruppen, setzeGruppen] = useState<readonly string[]>([]);
  const [art, setzeArt] = useState<'kunde' | 'rabattgruppe'>('rabattgruppe');
  const [gruppe, setzeGruppe] = useState('');
  const [suche, setzeSuche] = useState('');
  const [treffer, setzeTreffer] = useState<readonly { id: string; text: string }[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  /** CRM-104: die Zuordnung, deren Entfernen im Pop-up nachfragt. */
  const [zuEntfernen, setzeZuEntfernen] = useState<Preislistenzuordnung | null>(null);

  const laden = useCallback(async (): Promise<void> => {
    try {
      const [z, r] = await Promise.all([holeZuordnungen(liste.id), holeRabattgruppen()]);
      setzeZeilen(z);
      setzeGruppen(r);
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }, [liste.id]);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function suchen(): Promise<void> {
    if (suche.trim().length < 3) {
      setzeTreffer([]);
      return;
    }
    try {
      const ergebnis = await kundenliste({ suche: suche.trim() });
      setzeTreffer(
        ergebnis.kunden.slice(0, 10).map((k) => ({
          id: k.id,
          text: `${k.kundennummer} · ${k.name}`,
        })),
      );
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }

  async function hinzufuegen(ziel: {
    kunde: string | null;
    rabattgruppe: string | null;
  }): Promise<void> {
    try {
      await zuordnungAnlegen(liste.id, ziel);
      setzeSuche('');
      setzeTreffer([]);
      setzeGruppe('');
      await laden();
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }

  return (
    <Dialog
      titel={`Zuordnungen zu ${liste.kennung}`}
      unterzeile={liste.bezeichnung}
      breite="breit"
      beiSchliessen={beiSchliessen}
    >
      {fehler !== undefined && <Meldung fehler={fehler} />}

      {zeilen.length === 0 ? (
        <p className="hinweiszeile">
          Keine Zuordnung — die Liste gilt damit für <strong>alle</strong> Kunden, soweit
          keine höherrangige Liste greift.
        </p>
      ) : (
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle" aria-label="Zuordnungen">
            <thead>
              <tr>
                <th scope="col">Gilt für</th>
                <th scope="col">Art</th>
                {darfKonfigurieren && <th scope="col">Handlung</th>}
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z) => (
                <tr key={z.id}>
                  <th scope="row">{zuordnungsziel(z)}</th>
                  <td>{z.rabattgruppe === null ? 'Kunde' : 'Rabattgruppe'}</td>
                  {darfKonfigurieren && (
                    <td>
                      <button
                        className="knopf knopf--eingriff knopf--klein"
                        type="button"
                        onClick={() => setzeZuEntfernen(z)}
                      >
                        Entfernen …
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {darfKonfigurieren && (
        <fieldset>
          <legend>Zuordnung hinzufügen</legend>

          <div className="feld">
            <span className="feld__beschriftung">Wofür</span>
            <label htmlFor="zoRabatt">
              <input
                id="zoRabatt"
                type="radio"
                name="zuordnungsart"
                checked={art === 'rabattgruppe'}
                onChange={() => setzeArt('rabattgruppe')}
              />{' '}
              Für eine Rabattgruppe
            </label>{' '}
            <label htmlFor="zoKunde">
              <input
                id="zoKunde"
                type="radio"
                name="zuordnungsart"
                checked={art === 'kunde'}
                onChange={() => setzeArt('kunde')}
              />{' '}
              Für einen einzelnen Kunden
            </label>
          </div>

          {art === 'rabattgruppe' ? (
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="zoGruppe">
                Rabattgruppe
              </label>
              <select
                id="zoGruppe"
                className="feld__eingabe"
                value={gruppe}
                onChange={(e) => {
                  setzeGruppe(e.target.value);
                }}
              >
                <option value="">— wählen —</option>
                {gruppen.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
              {/* KUN-10 */}
              <p className="feld__hilfe">
                Die Auswahl kommt aus den tatsächlich vergebenen Gruppen am Kunden.
                Steht die gewünschte nicht dabei, ist sie noch an keinem Kunden
                hinterlegt.
              </p>
              <button
                className="knopf"
                type="button"
                disabled={gruppe === ''}
                onClick={() => void hinzufuegen({ kunde: null, rabattgruppe: gruppe })}
              >
                Rabattgruppe zuordnen
              </button>
            </div>
          ) : (
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="zoSuche">
                Kunde suchen
              </label>
              <input
                id="zoSuche"
                className="feld__eingabe"
                value={suche}
                onChange={(e) => {
                  setzeSuche(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void suchen();
                  }
                }}
              />
              {/* KUN-22 */}
              <p className="feld__hilfe">Ab drei Zeichen, dann Eingabetaste.</p>
              <button className="knopf" type="button" onClick={() => void suchen()}>
                Suchen
              </button>
              <ul className="rechteliste">
                {treffer.map((t) => (
                  <li key={t.id}>
                    <button
                      className="knopf knopf--klein"
                      type="button"
                      onClick={() => void hinzufuegen({ kunde: t.id, rabattgruppe: null })}
                    >
                      {t.text} zuordnen
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </fieldset>
      )}

      {zuEntfernen !== null && (
        <Rueckfrage
          frage="Zuordnung entfernen?"
          unterzeile={`${liste.kennung} · ${zuordnungsziel(zuEntfernen)}`}
          tat="Entfernen"
          beiBestaetigen={async () => {
            await zuordnungLoeschen(liste.id, zuEntfernen.id);
            setzeZuEntfernen(null);
            await laden();
          }}
          beiSchliessen={() => setzeZuEntfernen(null)}
        >
          {/*
            PRD-13: Ohne Zuordnung gilt die Liste für alle. Wer die letzte
            entfernt, öffnet sie also allen Kunden — das muss hier stehen.
          */}
          {zeilen.length === 1 ? (
            <p>
              Es ist die letzte Zuordnung. Danach gilt die Liste für <strong>alle</strong> Kunden,
              soweit keine höherrangige Liste greift.
            </p>
          ) : (
            <p>
              {zuEntfernen.rabattgruppe === null ? 'Der Kunde bekommt' : 'Die Rabattgruppe bekommt'} diese
              Liste nicht mehr; die Preisfindung greift auf die nächste zutreffende Liste zurück.{' '}
              {zeilen.length === 2 ? '1 Zuordnung bleibt.' : `${String(zeilen.length - 1)} Zuordnungen bleiben.`}
            </p>
          )}
        </Rueckfrage>
      )}
    </Dialog>
  );
}

function zuordnungsziel(z: Preislistenzuordnung): string {
  return z.rabattgruppe ?? `${z.kundennummer ?? ''} · ${z.kundenname ?? ''}`;
}

type Formular = {
  kennung: string;
  bezeichnung: string;
  rangstufe: Rangstufe;
  gueltigAb: string;
  gueltigBis: string;
  vorrangig: boolean;
};

function leer(): Formular {
  return {
    kennung: '',
    bezeichnung: '',
    rangstufe: 'aktion',
    gueltigAb: new Date().toISOString().slice(0, 10),
    gueltigBis: '',
    vorrangig: false,
  };
}

function ausListe(l: Preisliste): Formular {
  return {
    kennung: l.kennung,
    bezeichnung: l.bezeichnung,
    rangstufe: l.rangstufe,
    gueltigAb: l.gueltigAb,
    gueltigBis: l.gueltigBis ?? '',
    vorrangig: l.vorrangig,
  };
}
